/**
 * Where WS-5 invoices show up outside their own pages:
 *
 *   WsTicketInvoiceLink   on a customer-approved field ticket — "Not invoiced
 *                         yet" with a link to New invoice, or the invoice
 *                         that holds it. Read from the ticket itself: the API
 *                         names its live invoice (invoiceId / invoiceNumber /
 *                         invoiceStatus, fieldiq-api#1304). A customer-approved
 *                         ticket with no live invoice is exactly what
 *                         GET /invoices/billable lists, so null means ready.
 *   WsJobInvoicesSection  on a job's Tickets tab — the invoices whose job is
 *                         this one. An invoice billing tickets from several
 *                         jobs carries no job, so it is not listed here, and
 *                         the panel says that too.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { useWsInvoices } from '../../../hooks/useWsInvoices';
import { fmtCurrency } from '../../../lib/display-labels';
import { wsDay } from '../../../lib/well-servicing';
import { wsInvoiceStatusSkin } from '../../../lib/ws-invoices';
import type { WsInvoiceRow } from '../../../types/ws-invoices';
import type { WsTicket } from '../../../types/ws-tickets';
import { useOpenRecord } from '../../downhole/recordPage';
import { WsBadge, WsEmpty, WsPanel, WsRegister, type WsColumn } from '../WsBits';
import { WS_NEW_INVOICE_PATH } from './InvoicesScreen';

type TicketInvoiceRef = Pick<WsTicket, 'id' | 'invoiceId' | 'invoiceNumber' | 'invoiceStatus' | 'invoiceStatusLabel'>;

export function WsTicketInvoiceLink({ ticket }: { ticket: TicketInvoiceRef }) {
  let body: React.ReactNode;
  if (ticket.invoiceId === undefined) {
    // An API without the field: absent is unknown, never "not invoiced".
    body = 'Whether this ticket is invoiced could not be read.';
  } else if (ticket.invoiceId) {
    body = (
      <>
        On invoice{' '}
        <Link className="font-mono font-bold underline" to={`/well-servicing/invoices/${ticket.invoiceId}`} data-testid="ws-ticket-invoice-holder">
          {ticket.invoiceNumber || '—'}
        </Link>{' '}
        ({ticket.invoiceStatusLabel || ticket.invoiceStatus || '—'}).
      </>
    );
  } else {
    body = (
      <>
        Not invoiced yet.{' '}
        <Link className="font-bold underline" to={`${WS_NEW_INVOICE_PATH}?ticket=${ticket.id}`} data-testid="ws-ticket-invoice-ready">
          Put it on a new invoice
        </Link>
        .
      </>
    );
  }

  return (
    <div role="note" data-testid="ws-ticket-invoice" className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-text">
      <p>{body}</p>
    </div>
  );
}

const jobInvoiceColumns: WsColumn<WsInvoiceRow>[] = [
  { key: 'number', header: 'Invoice', render: (r) => <span className="font-mono font-extrabold text-white">{r.invoiceNumber || '—'}</span> },
  { key: 'status', header: 'Status', render: (r) => <WsBadge skin={wsInvoiceStatusSkin(r.status)} value={r.status} /> },
  { key: 'date', header: 'Invoice date', render: (r) => wsDay(r.invoiceDate) },
  { key: 'due', header: 'Due', render: (r) => wsDay(r.dueDate) },
  { key: 'total', header: 'Total', align: 'right', render: (r) => <span className="font-mono">{fmtCurrency(r.total, r.currency)}</span> },
];

export function WsJobInvoicesSection({ jobId }: { jobId: string }) {
  const openRecord = useOpenRecord();
  const query = useWsInvoices({ job_id: jobId });
  const rows = query.data?.data ?? [];
  return (
    <WsPanel
      eyebrow="Billed"
      title="Invoices"
      testId="ws-job-invoices"
      footer="An invoice billing tickets from several jobs is not listed here: find it on the Invoices register."
    >
      <WsRegister
        query={query}
        rows={rows}
        columns={jobInvoiceColumns}
        rail={(r) => wsInvoiceStatusSkin(r.status).rail}
        onOpen={(r) => openRecord(`/well-servicing/invoices/${r.id}`)}
        subject="this job's invoices"
        testId="ws-job-invoice"
        minWidth="min-w-[640px]"
        empty={<WsEmpty>No invoice bills this job yet.</WsEmpty>}
        card={(r) => (
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate font-mono text-sm font-extrabold text-white">{r.invoiceNumber || '—'}</p>
              <p className="mt-0.5 text-xs text-fieldiq-muted">Dated {wsDay(r.invoiceDate)} · {fmtCurrency(r.total, r.currency)}</p>
            </div>
            <WsBadge skin={wsInvoiceStatusSkin(r.status)} value={r.status} />
          </div>
        )}
      />
    </WsPanel>
  );
}
